import api from './api';
import type { OutputChannelConfig } from './output-channels-api';
export type { OutputChannelConfig, OutputChannelOption } from './output-channels-api';

// ============================================================================
// ROUTINES API — saved prompts that an agent runs on a schedule and delivers
// to one or more output channels (chat is always implicit).
//
// Backend route base: /api/routines
// Owning user is server-derived from the auth context; routines are personal
// (filtered by both organization_id AND user_id).
//
// @see docs/ROUTINES_REDESIGN_PLAN.md
// ============================================================================

// ============================================================================
// TYPES
// ============================================================================

export interface Routine {
  id: string;
  organization_id: string;
  /** Owning user (sourced from analysis_jobs.created_by on the backend) */
  created_by: string;
  name: string;
  schedule: string;
  timezone: string;
  message: string;
  outputChannels: OutputChannelConfig[];
  status: 'active' | 'inactive';
  last_run?: string;
  next_run?: string;
  error_count: number;
  last_error?: string;
  birthed_from_chat_session_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateRoutineDto {
  name: string;
  schedule: string;
  timezone?: string;
  message: string;
  outputChannels?: OutputChannelConfig[];
  /** When the routine is created via the agent propose-confirm flow, the
   *  chat thread where the proposal was approved. NULL for direct UI creation. */
  birthed_from_chat_session_id?: string | null;
}

export interface UpdateRoutineDto {
  name?: string;
  schedule?: string;
  timezone?: string;
  message?: string;
  enabled?: boolean;
  outputChannels?: OutputChannelConfig[];
}

// ============================================================================
// API SERVICE
// ============================================================================

export const routinesApi = {
  async listRoutines(): Promise<{ routines: Routine[] }> {
    const response = await api.get('/api/routines');
    return { routines: response.data.data || [] };
  },

  async getRoutine(routineId: string): Promise<{ routine: Routine }> {
    const response = await api.get(`/api/routines/${routineId}`);
    return { routine: response.data.data };
  },

  async createRoutine(data: CreateRoutineDto): Promise<{ routine: Routine }> {
    const response = await api.post('/api/routines', data);
    return { routine: response.data.data };
  },

  async updateRoutine(routineId: string, data: UpdateRoutineDto): Promise<{ routine: Routine }> {
    const response = await api.patch(`/api/routines/${routineId}`, data);
    return { routine: response.data.data };
  },

  async enableRoutine(routineId: string): Promise<{ success: boolean }> {
    const response = await api.post(`/api/routines/${routineId}/enable`);
    return { success: response.data.success };
  },

  async disableRoutine(routineId: string): Promise<{ success: boolean }> {
    const response = await api.post(`/api/routines/${routineId}/disable`);
    return { success: response.data.success };
  },

  async deleteRoutine(routineId: string): Promise<{ success: boolean }> {
    const response = await api.delete(`/api/routines/${routineId}`);
    return { success: response.data.success };
  },

  async executeRoutine(routineId: string): Promise<{
    success: boolean;
    message: string;
    data?: { thread_id?: string };
  }> {
    const response = await api.post(`/api/routines/${routineId}/execute`);
    return response.data;
  },
};

// ============================================================================
// HELPERS — temporary cron presets used by the legacy single-select picker.
// Task 10 replaces these with a friendly schedule picker driven by `cronstrue`
// and `cron-parser` for human-readable labels and live next-run previews.
// ============================================================================

export const ROUTINE_SCHEDULE_PRESETS = [
  { value: '0 6 * * *', label: 'Daily at 6 AM' },
  { value: '0 8 * * *', label: 'Daily at 8 AM' },
  { value: '0 9 * * *', label: 'Daily at 9 AM' },
  { value: '0 18 * * *', label: 'Daily at 6 PM' },
  { value: '0 0 * * 1', label: 'Weekly (Monday midnight)' },
  { value: '0 9 * * 1', label: 'Weekly (Monday 9 AM)' },
  { value: '0 0 1 * *', label: 'Monthly (1st at midnight)' },
  { value: '0 9 1 * *', label: 'Monthly (1st at 9 AM)' },
];

export function getScheduleLabel(cron: string): string {
  const preset = ROUTINE_SCHEDULE_PRESETS.find(p => p.value === cron);
  return preset?.label || cron;
}

export const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central European Time' },
];

export default routinesApi;
